import * as React from 'react';

import type { CguSignaturesExportFilter } from '@/api/exports-client';
import { useExportsClient } from '@/api/exports-client-context';
import type { AdminSignatureJournalEntryDto } from '@/api/legal-admin-client';
import type { AdminListItemDto } from '@/api/supplier-client';
import {
  useCguSignaturesJournal,
  useCguSignedContacts,
  useCguSignedStatus,
  useCguVersions,
} from '@/api/use-legal-admin';
import { useSuppliersList } from '@/api/use-suppliers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateTime, formatOpaqueId } from '@/routes/admin/cgu/cgu-format';
import { ForceResignatureAction } from '@/routes/admin/cgu/force-resignature-action';
import { useActiveContactsResolver } from '@/routes/admin/cgu/use-active-contacts-resolver';
import { useSignerIdentityResolver } from '@/routes/admin/cgu/use-signer-identity-resolver';

// =============================================================================
// cgu-signatures-route.tsx — Administration CGU (DBU §2.9).
// =============================================================================
// Route `/admin/cgu/signatures`. DEUX vues, composées DANS LE FRONT :
//
//   1. REGISTRE DES SIGNATURES (journal §2.9) — toutes les acceptances cross-
//      fournisseur, via le nouvel endpoint ADMIN legal (useCguSignaturesJournal,
//      withAdmin côté API). Lecture seule (l'admin ne signe jamais — GATE_0 S5bis).
//
//   2. NON-SIGNATAIRES — anti-join ALL réalisé ICI (isolation des schémas, invariant n°2) :
//      FIX-FOU-2 / DEC-46/DEC-48 — la signature CGU passe du grain SIRET au grain CONTACT.
//      Un SIRET est « signataire » SSI TOUS ses contacts ACTIFS ont signé la version courante ;
//      il est donc listé non-signataire dès qu'≥1 de ses contacts ACTIFS n'a pas signé. Le calcul
//      croise TROIS sources qui ne se parlent JAMAIS côté serveur : la LISTE des fournisseurs
//      (supplier-reference, useSuppliersList) ; les contactIds AYANT SIGNÉ par SIRET (legal,
//      useCguSignedContacts.bySiret) ; les contactIds ACTIFS par SIRET (supplier-reference,
//      fan-out getEstablishment → useActiveContactsResolver). Le BC legal ne connaît PAS l'état
//      Actif des contacts (invariant n°2) : la sémantique ALL est composée FRONT-ONLY.
//
// COLONNES issues du merge FRONT (structure FIGÉE) :
//   (a) « Dernière connexion » : CÂBLÉE — `masterLastLoginAt` (LastConnectionService, MAX(started_at)
//       de la session du MASTER, dérivé read-time DISPLAY-only) porté par la liste fournisseurs. « — »
//       « Jamais connecté » si aucun login.
//   (b) « Campagne en cours impactée » : PLACEHOLDER « — » (BC Campaign, câblage S10).
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope>, libellés texte porteurs d'info.

export function CguSignaturesRoute(): React.JSX.Element {
  return (
    <div className="space-y-10">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Administration CGU
        </h1>
        <p className="text-muted-foreground">
          Suivi cross-fournisseur des acceptations de CGU : registre des signatures
          et fournisseurs n'ayant pas signé la version courante.
        </p>
      </header>

      <CguSignaturesContent />
    </div>
  );
}

/**
 * Contenu du journal SANS l'en-tête h1 : réutilisable comme onglet « Journal » du registre CGU
 * (les sections portent leurs propres h2, un seul h1 par page — a11y ordre des titres).
 */
export function CguSignaturesContent(): React.JSX.Element {
  return (
    <div className="space-y-10">
      <SignaturesJournalSection />
      <NonSignatairesSection />
    </div>
  );
}

// --- Vue 1 — Registre des signatures (journal §2.9) ---
//
// Filtres (version / fournisseur / période) + tri (toutes colonnes) + export CSV des signatures
// FILTRÉES (DBU l.2110-2116). L'IDENTITÉ nominative (nom + email) est résolue par COMPOSITION FRONT
// (useSignerIdentityResolver, fan-out supplier-reference) : le BC legal ne résout pas nom/email sous
// la Règle n°1. Le filtre courant est PROPAGÉ à l'export (/admin/cgu/export.csv) — display == export.

/** Colonnes triables du registre (tri front, état local). */
type JournalSortColumn = 'version' | 'date' | 'signataire' | 'siret' | 'ip';
/** Direction de tri. */
type JournalSortDirection = 'asc' | 'desc';

/** État de tri courant (colonne + direction). Défaut : date décroissante (parité ordre serveur). */
interface JournalSort {
  readonly column: JournalSortColumn;
  readonly direction: JournalSortDirection;
}

/** État des filtres (chaîne vide = pas de restriction sur l'axe, parité des inputs natifs). */
interface JournalFilterState {
  readonly version: string;
  readonly siret: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY_FILTER: JournalFilterState = { version: '', siret: '', from: '', to: '' };

/** Convertit l'état de filtre en filtre d'export (omet les axes vides — URL nue si aucun filtre). */
function toExportFilter(f: JournalFilterState): CguSignaturesExportFilter {
  return {
    ...(f.version !== '' ? { version: f.version } : {}),
    ...(f.siret !== '' ? { siret: f.siret } : {}),
    ...(f.from !== '' ? { from: f.from } : {}),
    ...(f.to !== '' ? { to: f.to } : {}),
  };
}

/**
 * Vrai si l'entrée satisfait le filtre courant. MÊME sémantique que le filtre SERVEUR
 * (cgu-admin-read.service.matchesJournalFilter) : display filtré == export filtré (DoD R3).
 */
function entryMatchesFilter(
  entry: AdminSignatureJournalEntryDto,
  f: JournalFilterState,
): boolean {
  if (f.version !== '' && entry.cguVersionId !== f.version) {
    return false;
  }
  if (f.siret !== '' && entry.signerSiret !== f.siret) {
    return false;
  }
  const day = entry.acceptedAt.slice(0, 10);
  if (f.from !== '' && day < f.from) {
    return false;
  }
  if (f.to !== '' && day > f.to) {
    return false;
  }
  return true;
}

/** Ligne enrichie pour l'affichage + le tri (identité résolue + libellé de version). */
interface JournalRowView {
  readonly entry: AdminSignatureJournalEntryDto;
  /** Libellé de version CGU-AAAA-MM (via registre), fallback = cguVersionId opaque. */
  readonly versionLabel: string;
  /** Nom complet résolu (composition front), ou null si non résolu. */
  readonly nomComplet: string | null;
  /** Email résolu (composition front), ou null si non résolu. */
  readonly email: string | null;
}

/** Clé de tri d'une colonne (string comparable, casse-insensible pour le nominatif). */
function sortKey(row: JournalRowView, column: JournalSortColumn): string {
  switch (column) {
    case 'version':
      return row.versionLabel;
    case 'date':
      return row.entry.acceptedAt;
    case 'signataire':
      return (row.nomComplet ?? formatOpaqueId(row.entry.signerContactId)).toLowerCase();
    case 'siret':
      return row.entry.signerSiret;
    case 'ip':
      return row.entry.signerIpMasked ?? '';
  }
}

function SignaturesJournalSection(): React.JSX.Element {
  const journal = useCguSignaturesJournal();
  const versions = useCguVersions();
  const exportsClient = useExportsClient();

  const [filter, setFilter] = React.useState<JournalFilterState>(EMPTY_FILTER);
  const [sort, setSort] = React.useState<JournalSort>({
    column: 'date',
    direction: 'desc',
  });

  const entries: readonly AdminSignatureJournalEntryDto[] = React.useMemo(
    () => journal.data?.entries ?? [],
    [journal.data],
  );

  // Fan-out d'identité sur les SIRET DISTINCTS du journal (composition front, pattern signed-status).
  const distinctSirets = React.useMemo(
    () => entries.map((e) => e.signerSiret),
    [entries],
  );
  const identity = useSignerIdentityResolver(distinctSirets);

  // Libellé de version : cguVersionId → versionNumber (CGU-AAAA-MM), jamais l'UUID nu (cgu-format §).
  const versionLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const v of versions.data ?? []) {
      map.set(v.cguVersionId, v.versionNumber);
    }
    return map;
  }, [versions.data]);

  // Options du filtre fournisseur : SIRET DISTINCTS du journal, libellés « raison sociale — SIRET ».
  // Value = SIRET exact (axe SERVEUR replicable) ; la raison sociale résolue n'est qu'un libellé.
  const fournisseurOptions = React.useMemo(() => {
    const sirets = [...new Set(entries.map((e) => e.signerSiret))].sort();
    return sirets.map((siret) => ({
      siret,
      label: `${identity.raisonSocialeBySiret.get(siret) ?? 'Raison sociale non résolue'} — ${siret}`,
    }));
  }, [entries, identity.raisonSocialeBySiret]);

  // Vue filtrée + triée. Le filtre est la MÊME sémantique que le serveur (display == export).
  const rows: readonly JournalRowView[] = React.useMemo(() => {
    const views = entries
      .filter((e) => entryMatchesFilter(e, filter))
      .map((entry): JournalRowView => {
        const resolved = identity.byContactId.get(entry.signerContactId);
        return {
          entry,
          versionLabel: versionLabelById.get(entry.cguVersionId) ?? entry.cguVersionId,
          nomComplet: resolved?.nomComplet ?? null,
          email: resolved?.email ?? null,
        };
      });
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...views].sort((a, b) => {
      const cmp = sortKey(a, sort.column).localeCompare(sortKey(b, sort.column), 'fr');
      // Tie-break stable sur acceptedAt puis SIRET (ordre déterministe).
      const tie =
        a.entry.acceptedAt.localeCompare(b.entry.acceptedAt) ||
        a.entry.signerSiret.localeCompare(b.entry.signerSiret);
      return factor * (cmp !== 0 ? cmp : tie);
    });
  }, [entries, filter, identity.byContactId, versionLabelById, sort]);

  const onSort = (column: JournalSortColumn): void => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
  };

  const onExport = (): void => {
    exportsClient.downloadCguSignatures(toExportFilter(filter));
  };

  return (
    <section aria-labelledby="journal-heading" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="journal-heading" className="text-lg font-medium">
          Registre des signatures
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={journal.isPending}
        >
          Exporter le CSV (signatures filtrées)
        </Button>
      </div>

      <JournalControls
        filter={filter}
        onChange={setFilter}
        versionOptions={(versions.data ?? [])
          // Le filtre Version du journal des signatures ne liste QUE les versions OPPOSABLES
          // (Publiée / Archivée) : un Brouillon n'a jamais été publié → aucune signature possible,
          // donc jamais une option de filtre pertinente. (Bug 2026-08-20 : un brouillon figurait.)
          .filter((v) => v.state !== 'Brouillon')
          .map((v) => ({
            cguVersionId: v.cguVersionId,
            versionNumber: v.versionNumber,
          }))}
        fournisseurOptions={fournisseurOptions}
      />

      {journal.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement du registre…
        </p>
      ) : journal.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement : {journal.error.message}
        </p>
      ) : entries.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune signature enregistrée pour le moment.
        </p>
      ) : rows.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune signature ne correspond aux filtres sélectionnés.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Registre des signatures de CGU (version, date et heure, identité du signataire,
              SIRET, adresse IP masquée). Colonnes triables.
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <SortableHeader
                  column="version"
                  label="Version signée"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  column="date"
                  label="Date et heure"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  column="signataire"
                  label="Signataire (nom et email)"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  column="siret"
                  label="SIRET signataire"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  column="ip"
                  label="Adresse IP (masquée)"
                  sort={sort}
                  onSort={onSort}
                />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ entry, versionLabel, nomComplet, email }) => (
                <tr
                  key={`${entry.signerSiret}-${entry.cguVersionId}-${entry.acceptedAt}`}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 align-top font-mono">{versionLabel}</td>
                  <td className="px-4 py-3 align-top">
                    {formatDateTime(entry.acceptedAt)}
                  </td>
                  {/* Identité résolue par composition front. Non résolu → contactId tronqué (dégradation). */}
                  <td className="px-4 py-3 align-top">
                    {nomComplet !== null ? (
                      <span className="flex flex-col">
                        <span className="font-medium text-foreground">{nomComplet}</span>
                        {email !== null ? (
                          <span className="text-muted-foreground">{email}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span
                        className="font-mono text-muted-foreground"
                        aria-label={`Identité non résolue (contact ${entry.signerContactId})`}
                      >
                        {formatOpaqueId(entry.signerContactId)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top font-mono">{entry.signerSiret}</td>
                  <td className="px-4 py-3 align-top font-mono">
                    {entry.signerIpMasked ?? (
                      <span
                        className="text-muted-foreground"
                        aria-label="Adresse IP non disponible"
                      >
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Option du filtre version (libellé fonctionnel CGU-AAAA-MM, valeur = cguVersionId opaque). */
interface VersionOption {
  readonly cguVersionId: string;
  readonly versionNumber: string;
}

/** Option du filtre fournisseur (valeur = SIRET exact, libellé = raison sociale résolue + SIRET). */
interface FournisseurOption {
  readonly siret: string;
  readonly label: string;
}

interface JournalControlsProps {
  readonly filter: JournalFilterState;
  readonly onChange: (next: JournalFilterState) => void;
  readonly versionOptions: readonly VersionOption[];
  readonly fournisseurOptions: readonly FournisseurOption[];
}

/**
 * Contrôles de filtre du registre (version / fournisseur / période). État remonté au parent
 * (handlers onChange, jamais de copie de l'état — L-053 R2). Chaque contrôle est LABELLISÉ (WCAG
 * 1.3.1 / 4.1.2). Le filtre fournisseur est un SELECT de SIRET (axe serveur replicable) libellé
 * par la raison sociale résolue côté front.
 */
function JournalControls({
  filter,
  onChange,
  versionOptions,
  fournisseurOptions,
}: JournalControlsProps): React.JSX.Element {
  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-1.5">
        <Label htmlFor="journal-filter-version">Version</Label>
        <select
          id="journal-filter-version"
          className={selectClass}
          value={filter.version}
          onChange={(e) => onChange({ ...filter, version: e.target.value })}
        >
          <option value="">Toutes les versions</option>
          {versionOptions.map((v) => (
            <option key={v.cguVersionId} value={v.cguVersionId}>
              {v.versionNumber}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="journal-filter-fournisseur">Fournisseur</Label>
        <select
          id="journal-filter-fournisseur"
          className={selectClass}
          value={filter.siret}
          onChange={(e) => onChange({ ...filter, siret: e.target.value })}
        >
          <option value="">Tous les fournisseurs</option>
          {fournisseurOptions.map((o) => (
            <option key={o.siret} value={o.siret}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="journal-filter-from">Période — du</Label>
        <Input
          id="journal-filter-from"
          type="date"
          value={filter.from}
          onChange={(e) => onChange({ ...filter, from: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="journal-filter-to">Période — au</Label>
        <Input
          id="journal-filter-to"
          type="date"
          value={filter.to}
          onChange={(e) => onChange({ ...filter, to: e.target.value })}
        />
      </div>
    </div>
  );
}

interface SortableHeaderProps {
  readonly column: JournalSortColumn;
  readonly label: string;
  readonly sort: JournalSort;
  readonly onSort: (column: JournalSortColumn) => void;
}

/**
 * En-tête de colonne triable. `aria-sort` porte l'état de tri (ascending/descending/none — WCAG
 * 1.3.1) ; le bouton interne porte l'action clavier (WCAG 2.1.1). L'indicateur ▲/▼ est aria-hidden
 * (le libellé + aria-sort portent déjà l'information — jamais la couleur/le glyphe seul).
 */
function SortableHeader({
  column,
  label,
  sort,
  onSort,
}: SortableHeaderProps): React.JSX.Element {
  const active = sort.column === column;
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? sort.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <th scope="col" aria-sort={ariaSort} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 text-left font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {label}
        <span aria-hidden="true">{active ? (sort.direction === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

// --- Vue 2 — Non-signataires (anti-join ALL front : fournisseurs ⨯ contacts Actifs ⨯ signataires) ---

// S29-CGU-NONSIGN — clés de tri client-side (colonnes PRÉSENTES uniquement ; JAMAIS les 2
// placeholders « Dernière connexion » / « Campagne en cours impactée » qui n'ont pas de donnée).
type NonSignSortKey =
  | 'siren'
  | 'raisonSocialeLegale'
  | 'siret'
  | 'masterNomComplet'
  | 'masterEmail'
  | 'signedVersion'
  | 'signedAt';

/** Direction de tri (par en-tête cliquable). */
type SortDir = 'asc' | 'desc';

/**
 * S29-CGU-NONSIGN — ligne MERGÉE côté FRONT (isolation n°2) : le fournisseur (supplier-reference,
 * dont l'identité nominative du MASTER `masterNomComplet`/`masterEmail`, A-03 admin-only) + son
 * statut de signature (legal : dernière version signée + date). Le merge se fait PAR SIRET ; les
 * deux BC ne se parlent JAMAIS (aucun import cross-BC, aucune requête cross-schéma).
 */
interface NonSignRow {
  readonly supplier: AdminListItemDto;
  /** Dernière version CGU signée par ce SIRET (possiblement antérieure), ou null = jamais signé. */
  readonly signedVersionId: string | null;
  /** Horodatage ISO de la dernière signature, ou null = jamais signé. */
  readonly signedAt: string | null;
}

/** Valeur de tri (chaîne comparable) d'une ligne mergée pour la colonne demandée. */
function nonSignSortValue(row: NonSignRow, key: NonSignSortKey): string {
  switch (key) {
    case 'siren':
      return row.supplier.siren;
    case 'raisonSocialeLegale':
      return (row.supplier.raisonSocialeLegale ?? '').toLowerCase();
    case 'siret':
      return row.supplier.siret;
    case 'masterNomComplet':
      return (row.supplier.masterNomComplet ?? '').toLowerCase();
    case 'masterEmail':
      return (row.supplier.masterEmail ?? '').toLowerCase();
    case 'signedVersion':
      return row.signedVersionId ?? '';
    case 'signedAt':
      // ISO 8601 : l'ordre lexicographique coïncide avec l'ordre chronologique ; null en dernier.
      return row.signedAt ?? '';
  }
}

function NonSignatairesSection(): React.JSX.Element {
  // T2-REGISTRE : le registre est désormais PAGINÉ (enveloppe `{items,…}`). L'anti-join des
  // non-signataires a besoin de TOUS les fournisseurs : on demande la 1ʳᵉ page à la taille max
  // du contrat (100). V1 (échelle 82×4) tient sous cette borne ; au-delà, un balayage multi-pages
  // serait requis (hors périmètre T2, borne serveur assumée).
  const suppliers = useSuppliersList({ page: 1, pageSize: 100 });
  // FIX-FOU-2 / DEC-48 — signataires de la version courante au grain CONTACT (bySiret) + version
  // courante d'affichage. Remplace l'anti-join SIRET (ANY) par le calcul ALL (contact-grain).
  const signedContacts = useCguSignedContacts();
  // S29 — statut de signature par SIRET (dernière version signée + date). Sert le MERGE (métadonnées
  // « dernière version signée / date » inchangées).
  const signedStatus = useCguSignedStatus();

  // DEC-48 — contacts ACTIFS par SIRET (fan-out getEstablishment, supplier-reference). Résolus ICI
  // car le BC legal ne connaît PAS l'état Actif des contacts (invariant n°2). Le tableau de SIRET
  // vient de la liste des fournisseurs (vide tant qu'elle charge → aucun fan-out prématuré).
  const supplierSirets = React.useMemo(
    () => suppliers.data?.items.map((s) => s.siret) ?? [],
    [suppliers.data],
  );
  const activeContacts = useActiveContactsResolver(supplierSirets);

  // Filtre plein-texte + tri client-side (échelle V1 82×4, parité S29-CGU-JOURNAL-SIGN).
  const [query, setQuery] = React.useState('');
  const [sortKey, setSortKey] = React.useState<NonSignSortKey>('siret');
  const [sortDir, setSortDir] = React.useState<SortDir>('asc');

  // L'anti-join ALL + le merge exigent QUATRE lectures (liste + signataires contact-grain + statut +
  // fan-out contacts Actifs). Tant que l'une charge / échoue, on l'indique.
  if (
    suppliers.isPending ||
    signedContacts.isPending ||
    signedStatus.isPending ||
    activeContacts.isPending
  ) {
    return (
      <section aria-labelledby="non-signataires-heading" className="space-y-4">
        <h2 id="non-signataires-heading" className="text-lg font-medium">
          Fournisseurs n'ayant pas signé
        </h2>
        <p role="status" className="text-muted-foreground">
          Chargement des non-signataires…
        </p>
      </section>
    );
  }
  if (
    suppliers.isError ||
    signedContacts.isError ||
    signedStatus.isError ||
    activeContacts.isError
  ) {
    const message =
      suppliers.error?.message ??
      signedContacts.error?.message ??
      signedStatus.error?.message ??
      (activeContacts.isError ? 'Lecture des contacts Actifs indisponible' : null) ??
      'Erreur inconnue';
    return (
      <section aria-labelledby="non-signataires-heading" className="space-y-4">
        <h2 id="non-signataires-heading" className="text-lg font-medium">
          Fournisseurs n'ayant pas signé
        </h2>
        <p role="alert" className="text-destructive">
          Erreur de chargement : {message}
        </p>
      </section>
    );
  }

  // ANTI-JOIN ALL front (isolation n°2) : un fournisseur est non-signataire dès qu'≥1 de ses contacts
  // ACTIFS n'a pas signé la version courante. Edge : 0 contact actif ⇒ `.some` sur vide = false ⇒ NON
  // listé (conforme au verbatim DEC-48 « ≥1 contact actif non-signataire »). Composition front-only.
  const signedContactsBySiret = signedContacts.data.bySiret;
  const status = signedStatus.data;
  // MERGE par SIRET : identité MASTER (supplier-reference) + dernière version/date (legal).
  const merged: NonSignRow[] = suppliers.data.items
    .filter((s) => {
      const signedIds = signedContactsBySiret[s.siret] ?? [];
      const activeIds = activeContacts.activeContactIdsBySiret.get(s.siret) ?? new Set<string>();
      return [...activeIds].some((contactId) => !signedIds.includes(contactId));
    })
    .map((s) => {
      const st = status[s.siret];
      return {
        supplier: s,
        signedVersionId: st?.signedVersionId ?? null,
        signedAt: st?.signedAt ?? null,
      };
    });

  // Filtre plein-texte (siren | raison sociale | siret | contact MASTER | email MASTER).
  const needle = query.trim().toLowerCase();
  const filtered =
    needle === ''
      ? merged
      : merged.filter((r) =>
          [
            r.supplier.siren,
            r.supplier.raisonSocialeLegale ?? '',
            r.supplier.siret,
            r.supplier.masterNomComplet ?? '',
            r.supplier.masterEmail ?? '',
          ]
            .join(' ')
            .toLowerCase()
            .includes(needle),
        );

  // Tri client-side (colonne présente) + tie-break SIRET ASC (ordre stable).
  const rows = [...filtered].sort((a, b) => {
    const va = nonSignSortValue(a, sortKey);
    const vb = nonSignSortValue(b, sortKey);
    const base = va < vb ? -1 : va > vb ? 1 : 0;
    const signedBase = sortDir === 'asc' ? base : -base;
    return signedBase !== 0
      ? signedBase
      : a.supplier.siret.localeCompare(b.supplier.siret);
  });

  const onSort = (key: NonSignSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <section aria-labelledby="non-signataires-heading" className="space-y-4">
      <h2 id="non-signataires-heading" className="text-lg font-medium">
        Fournisseurs n'ayant pas signé
      </h2>
      <p className="text-sm text-muted-foreground">
        Fournisseurs référencés dont au moins un contact actif n'a pas signé la
        version courante des CGU
        {signedContacts.data.currentVersionId !== null
          ? ` (${signedContacts.data.currentVersionId})`
          : ' (aucune version publiée)'}
        .
      </p>

      {merged.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Tous les fournisseurs référencés ont signé la version courante.
        </p>
      ) : (
        <>
          <div className="max-w-sm">
            <label
              htmlFor="non-signataires-filtre"
              className="mb-1 block text-sm font-medium"
            >
              Filtrer (SIREN, raison sociale, SIRET, contact ou email du MASTER)
            </label>
            <input
              id="non-signataires-filtre"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un non-signataire…"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          {rows.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucun non-signataire ne correspond à ce filtre.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="ds-table w-full border-collapse text-sm">
                <caption className="sr-only">
                  Fournisseurs n'ayant pas signé la version courante des CGU (SIREN,
                  raison sociale, SIRET, contact et email du MASTER, dernière version
                  signée, date de dernière signature, dernière connexion, campagne
                  impactée, action de resignature)
                </caption>
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                    <NonSignSortableHeader
                      label="SIREN"
                      column="siren"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="Raison sociale"
                      column="raisonSocialeLegale"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="SIRET"
                      column="siret"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="Contact MASTER"
                      column="masterNomComplet"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="Email MASTER"
                      column="masterEmail"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="Dernière version signée"
                      column="signedVersion"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <NonSignSortableHeader
                      label="Date dernière signature"
                      column="signedAt"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    {/* (a) Dernière connexion du MASTER (masterLastLoginAt, LastConnectionService).
                        DISPLAY-only (dérivée read-time, non SQL-triable). */}
                    <th scope="col" className="px-4 py-3 font-medium">
                      Dernière connexion
                    </th>
                    {/* PLACEHOLDER (b) : BC Campaign absent avant S10. NON triable. */}
                    <th scope="col" className="px-4 py-3 font-medium">
                      Campagne en cours impactée
                    </th>
                    {/* R15-GOV-04 — colonne d'ACTION admin (« Forcer la resignature »). NON triable. */}
                    <th scope="col" className="px-4 py-3 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.supplier.siret}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-4 py-3 align-top font-mono">
                        {row.supplier.siren}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {row.supplier.raisonSocialeLegale ?? (
                          <span className="text-muted-foreground">
                            Non renseignée
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top font-mono">
                        {row.supplier.siret}
                      </td>
                      {/* S29 — identité nominative du MASTER (A-03 admin-only, PII). */}
                      <td className="px-4 py-3 align-top">
                        {row.supplier.masterNomComplet ?? (
                          <span
                            className="text-muted-foreground"
                            aria-label="Contact du MASTER non disponible"
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top font-mono">
                        {row.supplier.masterEmail ?? (
                          <span
                            className="text-muted-foreground"
                            aria-label="Email du MASTER non disponible"
                          >
                            —
                          </span>
                        )}
                      </td>
                      {/* S29 — dernière version signée (legal). Jamais signé si aucune signature. */}
                      <td className="px-4 py-3 align-top font-mono">
                        {row.signedVersionId ?? (
                          <span className="text-muted-foreground">Jamais signé</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {row.signedAt !== null ? (
                          formatDate(row.signedAt)
                        ) : (
                          <span
                            className="text-muted-foreground"
                            aria-label="Date de dernière signature non disponible"
                          >
                            —
                          </span>
                        )}
                      </td>
                      {/* (a) Dernière connexion (login) du MASTER — donnée RÉELLE A2 (masterLastLoginAt,
                          LastConnectionService, DISPLAY-only). « — » si jamais connecté. */}
                      <td className="px-4 py-3 align-top">
                        {row.supplier.masterLastLoginAt !== null ? (
                          formatDate(row.supplier.masterLastLoginAt)
                        ) : (
                          <span
                            className="text-muted-foreground"
                            aria-label="Jamais connecté à la plateforme"
                          >
                            —
                          </span>
                        )}
                      </td>
                      {/* (b) Différé-visible « — » : source campagne câblée en S10. */}
                      <td
                        className="px-4 py-3 align-top text-muted-foreground"
                        aria-label="Campagne en cours impactée non disponible"
                      >
                        —
                      </td>
                      {/* R15-GOV-04 — action admin ciblée : force la resignature (motif obligatoire). */}
                      <td className="px-4 py-3 align-top">
                        <ForceResignatureAction siret={row.supplier.siret} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * En-tête de colonne TRIABLE (WCAG : `aria-sort` sur le `<th>`, un `<button>` porteur du libellé).
 * Réservé aux colonnes PRÉSENTES (les 2 placeholders différés restent des `<th>` inertes).
 */
function NonSignSortableHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
}: {
  readonly label: string;
  readonly column: NonSignSortKey;
  readonly sortKey: NonSignSortKey;
  readonly sortDir: SortDir;
  readonly onSort: (key: NonSignSortKey) => void;
}): React.JSX.Element {
  const active = sortKey === column;
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? sortDir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <th scope="col" className="px-4 py-3 font-medium" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 font-medium hover:underline"
      >
        {label}
        <span aria-hidden="true">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** Formate un horodatage ISO en date/heure locale lisible (FR). Fallback : la chaîne brute. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}
